import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Award, ChevronRight, Star, Lock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CareerLevels = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/gamification/profile`, { withCredentials: true });
      setProfile(response.data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const levelDescriptions = {
    1: "Başlangıç seviyesi. Temel kurallar ve basit İngilizce kelimeler.",
    2: "İlk seviye atlama! Günlük görevler ve temel quiz'ler açılır.",
    3: "Acil durum senaryoları ve yolcu hizmetleri modülleri.",
    4: "Resmi ünvan! Standart mülakat soruları ve hikaye modu.",
    5: "Karmaşık senaryolar ve ileri İngilizce pratikler.",
    6: "Liderlik ve ekip yönetimi eğitimleri açılır.",
    7: "Kabin ekibi lideri. Gerçek mülakat simülasyonları.",
    8: "En zor senaryolar ve uluslararası standartlar.",
    9: "Uçuş süpervizörü. Stres yönetimi ileri seviye.",
    10: "En üst seviye! Tüm içerikler açık, özel davetli içerikler."
  };

  const levelRewards = {
    1: "İlk avatar uniformu",
    2: "Stajyer rozeti + ilk uçuş rotası",
    3: "Yeni uniforma parçası (yelek)",
    4: "Tam kabin memuru uniformu + profil çerçevesi",
    5: "Özel şapka + bonus puan çarpanı",
    6: "Purser rozeti + haftalık ekstra çark hakkı",
    7: "Altın rozet + sanal mağazada %20 indirim",
    8: "Kırmızı şeritli uniform + lider tablosunda öne çıkma",
    9: "Süpervizör rozeti + özel rota",
    10: "Platin rozet, özel animasyon, diploma sertifikası"
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  const currentLevel = profile?.career_level?.level || 1;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className={`bg-gradient-to-r ${profile?.career_level?.color || 'from-purple-500 to-violet-600'} pb-8 rounded-b-[2rem]`}>
        <div className="max-w-4xl mx-auto px-4 pt-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Geri</span>
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-lg text-4xl">
              {profile?.career_level?.icon || '👶'}
            </div>
            <div>
              <p className="text-white/80 text-sm">Mevcut Seviye</p>
              <h1 className="text-2xl font-bold text-white">{profile?.career_level?.name}</h1>
              <p className="text-white/90 text-lg font-semibold mt-1">
                {profile?.total_miles?.toLocaleString()} Mil
              </p>
            </div>
          </div>
          
          {/* Progress to next level */}
          {profile?.next_level && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-white/80 mb-1">
                <span>Sonraki: {profile.next_level.name}</span>
                <span>{profile.miles_to_next.toLocaleString()} mil kaldı</span>
              </div>
              <div className="bg-white/20 rounded-full h-3">
                <div 
                  className="bg-white h-3 rounded-full transition-all"
                  style={{ width: `${profile.level_progress}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 -mt-4">
        {/* All Levels */}
        <div className="space-y-3">
          {profile?.all_levels?.map((level) => {
            const isUnlocked = profile.total_miles >= level.min_miles;
            const isCurrent = level.level === currentLevel;
            
            return (
              <Card 
                key={level.level}
                className={`border-0 shadow-lg transition-all ${
                  isCurrent 
                    ? `ring-2 ring-offset-2 bg-gradient-to-r ${level.color} text-white` 
                    : isUnlocked 
                      ? 'bg-white' 
                      : 'bg-gray-100 opacity-60'
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    {/* Level Icon */}
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${
                      isCurrent ? 'bg-white/20' : isUnlocked ? `bg-gradient-to-br ${level.color} text-white` : 'bg-gray-200'
                    }`}>
                      {isUnlocked ? level.icon : <Lock className="w-6 h-6 text-gray-400" />}
                    </div>
                    
                    {/* Level Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-bold ${isCurrent ? 'text-white' : 'text-gray-800'}`}>
                          {level.name}
                        </h3>
                        {isCurrent && (
                          <span className="bg-white/30 text-white text-xs px-2 py-0.5 rounded-full">
                            Mevcut
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${isCurrent ? 'text-white/80' : 'text-gray-500'}`}>
                        {level.min_miles.toLocaleString()} - {level.max_miles.toLocaleString()} Mil
                      </p>
                      
                      {/* Description - show for current or hover */}
                      {(isCurrent || isUnlocked) && (
                        <p className={`text-xs mt-1 ${isCurrent ? 'text-white/70' : 'text-gray-400'}`}>
                          {levelDescriptions[level.level]}
                        </p>
                      )}
                      
                      {/* Reward */}
                      {isUnlocked && (
                        <div className={`flex items-center gap-1 mt-2 ${isCurrent ? 'text-white/90' : 'text-amber-600'}`}>
                          <Award className="w-4 h-4" />
                          <span className="text-xs font-medium">{levelRewards[level.level]}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Status Icon */}
                    {isUnlocked && !isCurrent && (
                      <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CareerLevels;
